import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  InputAdornment,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoIcon from '@mui/icons-material/Info';

// 格式化数字为千分位
const formatNumber = (num) => {
  if (num === undefined || num === null) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// 加仓成本计算器组件
const AverageCostCalculator = ({ tradingSettings = { market: 'A股', commissionRate: 0.03, stampDutyRate: 0.1, platformFee: 0, minFee: 0 } }) => {
  const theme = useTheme();
  
  // 通用手续费计算函数
  const calculateTradingFees = (amount, shares, isBuy = true) => {
    const market = tradingSettings.market || 'A股';
    let commission = 0;
    let stampDuty = 0;
    let totalFee = 0;
    
    if (market === 'A股') {
      // A股费率计算
      commission = amount * (tradingSettings.commissionRate / 100);
      stampDuty = isBuy ? 0 : amount * (tradingSettings.stampDutyRate / 100); // 买入不收印花税
      totalFee = commission + stampDuty;
    } else if (market === '港股') {
      // 港股费率计算
      commission = amount * (tradingSettings.commissionRate / 100);
      stampDuty = amount * (tradingSettings.stampDutyRate / 100); // 买卖都收印花税
      totalFee = commission + stampDuty;
    } else if (market === '美股') {
      // 美股费率计算
      const perShareFee = shares * tradingSettings.platformFee;
      totalFee = Math.max(perShareFee, tradingSettings.minFee);
      commission = totalFee; // 美股没有单独的佣金和印花税概念
      stampDuty = 0;
    }
    
    return {
      commission: commission,
      stampDuty: stampDuty,
      totalFee: totalFee
    };
  };
  
  // 状态定义
  const [currentShares, setCurrentShares] = useState(1000); // 当前持有股数
  const [currentCost, setCurrentCost] = useState(10.00); // 当前成本价
  const [additionalShares, setAdditionalShares] = useState(500); // 计划买入股数
  const [additionalCost, setAdditionalCost] = useState(8.00); // 计划买入价格
  const [result, setResult] = useState(null); // 计算结果
  const [showIntro, setShowIntro] = useState(false); // 控制介绍显示

  // 验证输入是否为有效数字
  const isValidNumber = (value) => {
    return !isNaN(value) && value > 0;
  };

  // 重置表单
  const handleReset = () => {
    setCurrentShares(1000);
    setCurrentCost(10.00);
    setAdditionalShares(500);
    setAdditionalCost(8.00);
    setResult(null);
  };

  // 计算加仓成本
  const calculateAverageCost = () => {
    // 验证输入
    if (!isValidNumber(currentShares) || !isValidNumber(currentCost) || 
        !isValidNumber(additionalShares) || !isValidNumber(additionalCost)) {
      return;
    }

    // 计算原有持仓成本
    const originalCost = currentShares * currentCost;
    
    // 计算新买入股票的成本（包含手续费）
    const newSharesCost = additionalShares * additionalCost;
    const fees = calculateTradingFees(newSharesCost, additionalShares, true);
    const totalNewCost = newSharesCost + fees.totalFee;
    
    // 计算总成本和总股数
    const totalCost = originalCost + totalNewCost;
    const totalShares = currentShares + additionalShares;
    
    // 计算新的平均成本
    const newAverageCost = totalCost / totalShares;
    
    // 计算成本变化
    const costChange = newAverageCost - currentCost;
    const costChangePercentage = (costChange / currentCost) * 100;

    setResult({
      totalShares,
      totalCost: totalCost.toFixed(2),
      newAverageCost: newAverageCost.toFixed(2),
      costChange: costChange.toFixed(2),
      costChangePercentage: costChangePercentage.toFixed(2),
      commission: fees.commission.toFixed(2),
      stampDuty: fees.stampDuty.toFixed(2),
      totalFees: fees.totalFee.toFixed(2),
      // 添加格式化后的值用于显示
      formattedTotalShares: formatNumber(totalShares),
      formattedTotalCost: formatNumber(totalCost.toFixed(2)),
      formattedNewAverageCost: formatNumber(newAverageCost.toFixed(2)),
      formattedCostChange: formatNumber(Math.abs(costChange).toFixed(2)),
      formattedOriginalCost: formatNumber(originalCost.toFixed(2)),
      formattedNewSharesCost: formatNumber(newSharesCost.toFixed(2)),
      formattedCommission: formatNumber(fees.commission.toFixed(2)),
      formattedStampDuty: formatNumber(fees.stampDuty.toFixed(2)),
      formattedTotalFees: formatNumber(fees.totalFee.toFixed(2)),
      formattedTotalNewCost: formatNumber(totalNewCost.toFixed(2))
    });
  };

  // 切换介绍显示
  const toggleIntro = () => {
    setShowIntro(!showIntro);
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* 计算器表单 */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CalculateIcon sx={{ mr: 1 }} />
              加仓成本计算器
              <Tooltip title="查看加仓成本计算器介绍">
                <IconButton size="small" onClick={toggleIntro} sx={{ ml: 1 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="当前持有股数"
                  type="number"
                  fullWidth
                  value={currentShares}
                  onChange={(e) => setCurrentShares(Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="当前成本价"
                  type="number"
                  fullWidth
                  value={currentCost}
                  onChange={(e) => setCurrentCost(Number(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="计划买入股数"
                  type="number"
                  fullWidth
                  value={additionalShares}
                  onChange={(e) => setAdditionalShares(Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="计划买入价格"
                  type="number"
                  fullWidth
                  value={additionalCost}
                  onChange={(e) => setAdditionalCost(Number(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={calculateAverageCost}
                    startIcon={<CalculateIcon />}
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    计算
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    startIcon={<RestartAltIcon />}
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    重置
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 计算结果 */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              计算结果
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            {result ? (
              <Card sx={{ mb: 3, bgcolor: theme.palette.primary.light + '15' }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    加仓成本结果
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body2">总持仓数量: {result.formattedTotalShares}股</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">总投资金额: ¥{result.formattedTotalCost}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        新的平均成本价: ¥{result.formattedNewAverageCost}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography 
                        variant="body2" 
                        color={Number(result.costChange) <= 0 ? 'success.main' : 'error.main'}
                      >
                        成本变化: {Number(result.costChange) <= 0 ? '下降' : '上升'} ¥{result.formattedCostChange}
                        ({Math.abs(Number(result.costChangePercentage)).toLocaleString('zh-CN')}%)
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">原始成本: ¥{result.formattedOriginalCost}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">新增成本: ¥{result.formattedTotalNewCost}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">佣金: ¥{result.formattedCommission} | 印花税: ¥{result.formattedStampDuty}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                请填写参数并点击计算按钮查看结果
              </Alert>
            )}

            {showIntro && (
              <Box sx={{ mt: 3 }} id="average-cost-intro">
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  加仓成本计算器说明
                </Typography>
                <Typography variant="body2" paragraph>
                  加仓成本计算器帮助投资者计算在已有持仓的基础上，追加买入后的新平均成本价。
                </Typography>
                <Typography variant="body2" paragraph>
                  当股票价格下跌时，投资者可能会考虑补仓以降低平均持仓成本。这个计算器可以帮助您快速计算出补仓后的新平均成本。
                </Typography>
                <Typography variant="body2" paragraph>
                  使用方法：
                </Typography>
                <Typography variant="body2" component="div">
                  <ol>
                    <li>输入当前持有的股票数量</li>
                    <li>输入当前的成本价</li>
                    <li>输入计划买入的股票数量</li>
                    <li>输入计划买入的价格</li>
                    <li>点击"计算"按钮获取结果</li>
                  </ol>
                </Typography>
                <Typography variant="body2" paragraph>
                  计算公式：新平均成本 = (当前持有股数 × 当前成本价 + 计划买入股数 × 计划买入价格 + 佣金 + 印花税) ÷ (当前持有股数 + 计划买入股数)
                  <br/>
                  佣金 = 计划买入股数 × 计划买入价格 × 佣金比例
                  <br/>
                  印花税 = 计划买入股数 × 计划买入价格 × 印花税比例
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AverageCostCalculator;